// Ads (Phase 3): fetched once per session and at each new in-game day; house ads when nothing is
// booked or offline. Impressions count only when ≥50% visible for 1 s; events batched every 30 s.
// The kill switch: every 30 s we ask which campaigns are still live and drop the rest (< 1 min).
import { signal } from "@preact/signals";
import { HOUSE_ADS, type AdEvent, type ServedAd, type SlotId } from "@degen/shared/ads";
import { flag } from "@degen/shared/flags";
import { api } from "./api";
import { online } from "./env";
import { anonId } from "./identity";
import { track } from "./telemetry";

export const ALL_SLOTS: SlotId[] = ["world.billboard", "world.ticker", "chirp.sponsored", "farm.featured", "loading.sponsor", "phone.home.banner"];
export const ads = signal<Partial<Record<SlotId, ServedAd>>>({});
/** Ad card (tap on an ad) and the leaving-the-game interstitial. */
export const adCard = signal<ServedAd | null>(null);

const house = (slot: SlotId, i = 0): ServedAd => ({ ...HOUSE_ADS[i % HOUSE_ADS.length], slot });
const queue: (AdEvent & { at: number })[] = [];
const seen = new Set<string>(); // one impression per creative per slot per session-day

export function adFor(slot: SlotId): ServedAd | null {
  if (!flag("ads")) return null;
  return ads.value[slot] ?? house(slot, slot.length);
}

export async function loadAds() {
  if (!flag("ads")) return;
  if (!online()) {
    ads.value = Object.fromEntries(ALL_SLOTS.map((s, i) => [s, house(s, i)]));
    return;
  }
  try {
    const r = await api<{ ads: ServedAd[] }>("GET", `/ads?slots=${ALL_SLOTS.join(",")}&anon=${anonId}`, undefined, { auth: false, timeoutMs: 5000 });
    ads.value = Object.fromEntries(r.ads.map((a) => [a.slot, a]));
    seen.clear();
  } catch {
    /* keep what we have */
  }
}

/** Count an impression (call when the ad has been ≥50% visible for 1 s). */
export function impression(ad: ServedAd) {
  const k = `${ad.slot}:${ad.creativeId}`;
  if (seen.has(k)) return;
  seen.add(k);
  track("ad_impression", { slot: ad.slot, house: ad.house, campaign: ad.campaignId });
  if (ad.house || ad.campaignId <= 0) return;
  queue.push({ kind: "impression", slot: ad.slot, campaignId: ad.campaignId, creativeId: ad.creativeId, at: Date.now() });
}

export function click(ad: ServedAd) {
  track("ad_click", { slot: ad.slot, house: ad.house, campaign: ad.campaignId });
  if (!ad.house && ad.campaignId > 0) queue.push({ kind: "click", slot: ad.slot, campaignId: ad.campaignId, creativeId: ad.creativeId, at: Date.now() });
  adCard.value = ad;
}

async function flush() {
  if (!queue.length || !online()) return;
  const events = queue.splice(0, 200).map(({ at: _at, ...e }) => e);
  try {
    await api("POST", "/ads/events", { anon: anonId, events }, { auth: false });
  } catch {
    queue.unshift(...events.map((e) => ({ ...e, at: Date.now() }))); // retry later
  }
}

async function pollLive() {
  if (!online()) return;
  try {
    const { live } = await api<{ live: number[] }>("GET", "/ads/live", undefined, { auth: false, timeoutMs: 5000 });
    const cur = ads.value;
    let changed = false;
    const next = { ...cur };
    for (const [slot, ad] of Object.entries(cur) as [SlotId, ServedAd][]) {
      if (ad && !ad.house && !live.includes(ad.campaignId)) {
        next[slot] = house(slot);
        changed = true;
      }
    }
    if (changed) ads.value = next;
  } catch {
    /* offline: keep */
  }
}

export function startAds() {
  void loadAds();
  setInterval(() => void flush(), 30_000);
  setInterval(() => void pollLive(), 30_000);
  addEventListener("pagehide", () => void flush());
}

/** Image cache for billboard textures (lazy, after the world is ready). A failed load falls back to house. */
const imgCache = new Map<string, Promise<HTMLImageElement | null>>();
export function adImage(url: string): Promise<HTMLImageElement | null> {
  let p = imgCache.get(url);
  if (!p) {
    p = new Promise((res) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => res(img);
      img.onerror = () => res(null);
      img.src = url;
    });
    imgCache.set(url, p);
  }
  return p;
}
